import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertCircle, Loader, Clock, Eye, Download } from 'lucide-react';
import { useFileQueue } from '../contexts/AppContext';
import ViewResultsModal from './ViewResultsModal';
import { analytics, posthog } from '../services/analytics';
import clsx from 'clsx';
import { useRetryDetection, formatRetryMessage } from '../hooks/useRetryDetection';
import { useToast } from '../contexts/ToastContext';

const JobStatusPanel: React.FC = () => {
  const { state, clearJob } = useFileQueue();
  const { currentJob } = state;
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [visualProgress, setVisualProgress] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useToast();

  // Detect retry behavior from job polling patterns
  const retryState = useRetryDetection(
    currentJob?.id || '',
    currentJob?.status || '',
    currentJob?.progress || 0
  );

  // Track which retry counts we've already shown toasts for
  const shownRetryToasts = useRef(new Set<number>());

  // Enhanced visual progress system
  useEffect(() => {
    if (!currentJob) return;
    
    if (currentJob.status === 'processing') {
      // Initialize visual progress with actual progress or start from 0
      const actualProgress = currentJob.progress ?? 0;
      setVisualProgress(actualProgress);

      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Start incremental progress updates (1% every 3.5 seconds)
      intervalRef.current = setInterval(() => {
        setVisualProgress((prev) => {
          const actualProgress = currentJob.progress ?? 0;
          
          // If actual progress is higher than visual progress, jump to actual
          if (actualProgress > prev) {
            return actualProgress;
          }
          
          // Otherwise, increment by 1% but don't exceed 95% (leave room for real updates)
          if (prev < 95) {
            return prev + 1;
          }
          
          return prev;
        });
      }, 3500); // 3.5 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // For non-processing jobs, use actual progress
      setVisualProgress(currentJob.progress ?? 100);
      
      // Clear interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [currentJob]);

  // When actual progress updates, immediately update visual progress if it's higher
  useEffect(() => {
    if (!currentJob) return;
    
    const actualProgress = currentJob.progress ?? 0;
    if (actualProgress > visualProgress) {
      setVisualProgress(actualProgress);
    }
  }, [currentJob, visualProgress]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Show batched toast notification after 2nd retry
  useEffect(() => {
    if (!currentJob) {
      // Reset shown toasts when job clears
      shownRetryToasts.current.clear();
      return;
    }

    // Only show toast if:
    // 1. We've detected at least 2 retries
    // 2. We're currently in a retry state
    // 3. We haven't shown a toast for this retry count yet
    if (
      retryState.retryCount >= 2 &&
      retryState.isRetrying &&
      !shownRetryToasts.current.has(retryState.retryCount)
    ) {
      // Mark this retry count as notified
      shownRetryToasts.current.add(retryState.retryCount);

      // Show batched warning toast
      toast.warning(
        'AI Processing Slower Than Usual',
        formatRetryMessage(retryState.retryCount)
      );

      // Track analytics using PostHog SDK directly
      posthog.capture('retry_notification_shown', {
        job_id: currentJob.id,
        retry_count: retryState.retryCount,
        status: currentJob.status,
        progress: currentJob.progress,
      });
    }
  }, [currentJob, retryState.retryCount, retryState.isRetrying, toast]);

  // Don't render if no job (guard clause after all hooks)
  if (!currentJob) {
    return null;
  }

  const getStatusIcon = () => {
    switch (currentJob.status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'processing':
        return <Loader className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Loader className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = () => {
    switch (currentJob.status) {
      case 'pending':
        return 'border-yellow-200 bg-yellow-50';
      case 'processing':
        return 'border-blue-200 bg-blue-50';
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'error':
      case 'failed':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const handleViewResults = () => {
    console.log('View results for job:', currentJob.id);
    setShowResultsModal(true);
  };

  const handleDownload = async () => {
    console.log('Download results for job:', currentJob.id);

    // Track download start
    analytics.downloadStarted({
      jobId: currentJob.id,
      downloadType: 'zip'
    });

    try {
      // Direct download from Convex HTTP endpoint
      // IMPORTANT: HTTP routes use .convex.site, NOT .convex.cloud
      const convexUrl = process.env.REACT_APP_CONVEX_URL || 'https://upbeat-butterfly-447.convex.cloud';
      const convexSiteUrl = convexUrl.replace('.convex.cloud', '.convex.site');
      const downloadUrl = `${convexSiteUrl}/download?jobId=${currentJob.id}`;
      console.log('📥 Downloading from Convex:', downloadUrl);

      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `sortingcat-${currentJob.id}.zip`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Track successful download
      analytics.downloadCompleted({
        jobId: currentJob.id,
        success: true
      });
    } catch (error) {
      // Track failed download
      analytics.downloadCompleted({
        jobId: currentJob.id,
        success: false,
        error: error instanceof Error ? error.message : 'Download failed'
      });
      console.error('Download failed:', error);
    }
  };

  const formatDuration = (start: Date, end?: Date): string => {
    // Ensure dates are valid Date objects
    const startTime = start instanceof Date && !isNaN(start.getTime()) ? start : new Date();
    
    // For processing jobs without an end time, show elapsed time
    // For completed jobs, show total duration
    const endTime = end instanceof Date && !isNaN(end.getTime()) ? end : new Date();
    
    const durationMs = endTime.getTime() - startTime.getTime();
    let seconds = Math.floor(Math.abs(durationMs) / 1000); // Use Math.abs to prevent negative durations
    
    // Cap at 59 minutes 59 seconds to prevent unrealistic displays
    // This handles cases where job data might be stale or invalid
    const maxSeconds = 59 * 60 + 59; // 59m 59s
    if (seconds > maxSeconds) {
      seconds = maxSeconds;
    }
    
    if (seconds < 60) {
      return `${seconds}s`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  return (
    <>
      <div className={clsx('card mb-6 border-l-4', getStatusColor())}>
        <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                {currentJob.status === 'processing' ? 'Processing' : currentJob.status}
              </h3>
              <p className="text-sm text-gray-600">
                Job ID: {currentJob.id}
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-sm font-medium text-gray-700">
              {currentJob.fileCount} file{currentJob.fileCount !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-gray-500">
              {formatDuration(currentJob.createdAt, currentJob.completedAt)}
            </p>
          </div>
        </div>

        {currentJob.message && (
          <p className="text-sm text-gray-700 mb-4">
            {currentJob.message}
          </p>
        )}

        {/* Progress bar for processing status */}
        {currentJob.status === 'processing' && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progress</span>
              <span>{currentJob.progress !== undefined ? `${currentJob.progress}%` : 'Processing...'}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-1000 ease-out"
                style={{ 
                  width: `${Math.round(visualProgress)}%`
                }}
              />
            </div>
          </div>
        )}

        {/* Action buttons for completed jobs */}
        {currentJob.status === 'completed' && (
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex space-x-3">
              <button
                onClick={handleViewResults}
                className="btn-primary flex items-center space-x-2"
              >
                <Eye className="w-4 h-4" />
                <span>View Results</span>
              </button>
              
              <button
                onClick={handleDownload}
                className="btn-secondary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download ZIP</span>
              </button>
            </div>
            
            <button
              onClick={clearJob}
              className="btn-outline flex items-center space-x-2 border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <span>Start New Job</span>
            </button>
          </div>
        )}

        {/* Error state actions */}
        {(currentJob.status === 'error' || currentJob.status === 'failed') && (
          <div className="flex space-x-3">
            <button
              onClick={clearJob}
              className="btn-primary"
            >
              Start New Job
            </button>
          </div>
        )}

        {/* Close button for completed jobs */}
        {currentJob.status === 'completed' && (
          <button
            onClick={clearJob}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            title="Close status panel"
            aria-label="Close job status"
          >
            ×
          </button>
        )}
      </div>
    </div>
    
    {/* View Results Modal */}
    {showResultsModal && currentJob && (
      <ViewResultsModal
        jobId={currentJob.id}
        isOpen={showResultsModal}
        onClose={() => setShowResultsModal(false)}
      />
    )}
  </>
  );
};

export default JobStatusPanel;